import FormControl from '@material-ui/core/FormControl';
import InputBase from '@material-ui/core/InputBase';
import ListItem from '@material-ui/core/ListItem';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { IVenture } from 'interfaces/venture';
import React, { ChangeEvent, memo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import { combineLatest } from 'rxjs';
import RxOp from 'rxjs-operators';
import selectedVentureService from 'services/profile';
import ventureService from 'services/venture';

interface IProps {
  disabled: boolean;
}

interface IObservableReturn {
  total: number,
  ventures: Partial<IVenture>[]
}

const BootstrapInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.primary.light,
      fontSize: 16,
      padding: '10px 26px 10px 12px',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:focus': {
        borderRadius: 4,
        borderColor: '#80bdff',
        boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
      },
    },
  }),
)(InputBase);

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    width: '100%',
  },
  menuItems: {
    padding: theme.spacing(2)
  }
}));

const VentureSelection = (props: IProps) => {
  const classes = useStyles({});
  const { disabled } = props;
  const [value, setValue] = useState('');
  const [ventures, setVentures] = useState([]);
  const history = useHistory();
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only('xs'));

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    const [id, name] = String(event.target.value).split('|');
    setValue(value);
    if (id && name) {
      selectedVentureService.setProfile({ id: Number(id), name }).pipe(
        RxOp.logError(),
      ).subscribe();
    }
  };

  const getValue = (id: number, name: string) => `${id}|${name}`;

  const [results, loading] = useObservable<IObservableReturn>(() => {
    return combineLatest([
      selectedVentureService.getSelectedVenture(),
      ventureService.getVenturesCount(),
      ventureService.list({ page: 0, pageSize: Number.MAX_SAFE_INTEGER })
    ]).pipe(
      RxOp.logError(),
      RxOp.map(([selection, total, venturesResponse]) => {
        const ventures = venturesResponse.results.map(v => ({ id: v.id, name: v.name }));
        setValue(getValue(selection.id, selection.name));
        return { total, ventures };
      })
    );
  }, [history]);

  useEffect(() => {
    if (!loading && !disabled && results === null) {
      ventureService.list({ page: 0, pageSize: Number.MAX_SAFE_INTEGER }).subscribe((data) => {
        setVentures(data.results);
        if (!value || !value.length) {
          selectedVentureService.getSelectedVenture().subscribe((selection) => {
            if (selection) {
              setValue(getValue(selection.id, selection.name));
            }
          });
        }
      });
    }
  }, [loading, disabled, results, value]);

  return (
    <ListItem disableGutters={!xs}>
      {loading && 'Carregando...'}
      {!loading && !disabled && ventures.length > 0 && (
        <FormControl className={classes.margin}>
          <Select
            value={value}
            onChange={handleChange}
            input={<BootstrapInput/>}
            fullWidth
          >
            {ventures.sort((a, b) => a.id - b.id).map(({ id, name }) => (
              <MenuItem key={`venture-select-${id}`} className={classes.menuItems} value={getValue(id, name)}>{name}</MenuItem>)
            )}
          </Select>
        </FormControl>
      )}

      {!loading && !disabled && !ventures.length && ('Nenhum empreendimento cadastrado')}

    </ListItem>
  );
};

export default memo<IProps>(VentureSelection);
